/**
 * Contractor Profile Types
 *
 * Types for enhanced contractor profile management including:
 * - Trade licenses
 * - Insurance policies
 * - Business information
 * - Document library
 */

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// LICENSE TYPES
// ============================================================================

export type LicenseType =
  | 'CGC'      // Certified General Contractor
  | 'CBC'      // Certified Building Contractor
  | 'CRC'      // Certified Roofing Contractor
  | 'EC'       // Electrical Contractor
  | 'ER'       // Electrical Registrant
  | 'CFC'      // Certified Plumbing Contractor
  | 'RF'       // Registered Plumbing Contractor
  | 'CAC'      // Class A Air Conditioning Contractor
  | 'CMC'      // Mechanical Contractor
  | 'CCC'      // Certified Specialty Contractor
  | 'other';   // Other license type

export type LicenseStatus =
  | 'active'
  | 'expired'
  | 'pending_renewal'
  | 'suspended'
  | 'inactive';

export interface ContractorLicense {
  id: string;
  licenseType: LicenseType;
  licenseNumber: string;
  qualifierName: string;          // Name on license
  state: string;                  // State issued (e.g., 'FL')
  issuedDate?: string;            // ISO date string
  expirationDate: string;         // ISO date string
  status: LicenseStatus;

  // Optional details
  tradeType?: string;             // 'electrical', 'plumbing', etc.
  county?: string;                // County jurisdiction if applicable
  specializations?: string[];     // Sub-specialties

  // Document reference
  documentId?: string;            // Reference to uploaded license copy
  documentUrl?: string;           // Direct URL to license document

  // Verification
  verifiedAt?: string;            // Last verification date
  verificationSource?: string;    // 'DBPR', 'manual', etc.

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// License type display labels
export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  CGC: 'Certified General Contractor',
  CBC: 'Certified Building Contractor',
  CRC: 'Certified Roofing Contractor',
  EC: 'Electrical Contractor',
  ER: 'Electrical Registrant',
  CFC: 'Certified Plumbing Contractor',
  RF: 'Registered Plumbing Contractor',
  CAC: 'Class A Air Conditioning Contractor',
  CMC: 'Mechanical Contractor',
  CCC: 'Certified Specialty Contractor',
  other: 'Other License',
};

// ============================================================================
// INSURANCE TYPES
// ============================================================================

export type InsuranceType =
  | 'general_liability'
  | 'workers_compensation'
  | 'professional_liability'
  | 'commercial_auto'
  | 'umbrella'
  | 'builders_risk'
  | 'other';

export type InsuranceStatus =
  | 'active'
  | 'expired'
  | 'pending_renewal'
  | 'cancelled';

export interface InsurancePolicy {
  id: string;
  insuranceType: InsuranceType;
  policyNumber: string;
  carrier: string;                // Insurance company name

  // Coverage amounts
  coverageAmount: number;         // Total coverage limit
  perOccurrenceLimit?: number;    // Per occurrence limit
  aggregateLimit?: number;        // Aggregate limit
  deductible?: number;

  // Dates
  effectiveDate: string;          // ISO date string
  expirationDate: string;         // ISO date string
  status: InsuranceStatus;

  // Named insured
  namedInsured: string;           // Company/person name on policy
  additionalInsured?: string[];   // Additional named insured

  // Document reference
  certificateDocumentId?: string; // Reference to COI document
  certificateUrl?: string;        // Direct URL to COI

  // Agent info
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;

  // Workers comp specific
  wcExempt?: boolean;             // WC exemption status
  wcExemptionDocId?: string;      // WC exemption form doc ID

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Insurance type display labels
export const INSURANCE_TYPE_LABELS: Record<InsuranceType, string> = {
  general_liability: 'General Liability',
  workers_compensation: "Workers' Compensation",
  professional_liability: 'Professional Liability (E&O)',
  commercial_auto: 'Commercial Auto',
  umbrella: 'Umbrella/Excess Liability',
  builders_risk: "Builder's Risk",
  other: 'Other Insurance',
};

// Minimum required coverage amounts by type (in dollars)
export const MINIMUM_COVERAGE_AMOUNTS: Partial<Record<InsuranceType, number>> = {
  general_liability: 300000,      // $300k minimum for most jurisdictions
  workers_compensation: 100000,   // Standard WC minimum
};

// ============================================================================
// CONTRACTOR PROFILE TYPES
// ============================================================================

export interface BusinessInfo {
  companyName: string;
  dbaName?: string;               // "Doing Business As" name
  businessType: 'sole_proprietor' | 'llc' | 'corporation' | 's_corp' | 'partnership';
  ein?: string;                   // Employer Identification Number

  // Address
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;

  // Contact
  phone: string;
  fax?: string;
  email: string;
  website?: string;

  // Additional
  yearEstablished?: number;
  employeeCount?: number;
}

export interface ContractorCredentials {
  licenses: ContractorLicense[];
  insurance: InsurancePolicy[];
  businessInfo?: BusinessInfo;

  // Overall status
  credentialsComplete: boolean;
  lastVerified?: string;          // ISO date of last verification check
}

// ============================================================================
// JOB TEMPLATE TYPES
// ============================================================================

export interface JobTemplate {
  id: string;
  name: string;
  description?: string;

  // Template data
  permitType: string;
  tradeCode: string;
  tradeName: string;
  jobScope?: string;
  scopeName?: string;

  // Pre-filled fields
  defaultPropertyType?: 'residential' | 'commercial';
  defaultCategory?: string;
  estimatedValueRange?: {
    min: number;
    max: number;
  };

  // Documents typically needed
  requiredDocuments?: string[];

  // Usage stats
  usageCount: number;
  lastUsedAt?: Timestamp;

  // Ownership
  contractorId: string;
  createdBy: string;
  isShared?: boolean;             // Shared with team

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// DOCUMENT LIBRARY TYPES
// ============================================================================

export type ReusableDocumentType =
  | 'license_copy'
  | 'insurance_coi'
  | 'wc_exemption'
  | 'business_license'
  | 'w9'
  | 'bond'
  | 'letterhead'
  | 'company_logo'
  | 'other';

export interface ReusableDocument {
  id: string;
  documentType: ReusableDocumentType;
  title: string;
  description?: string;

  // File info
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileType: string;               // MIME type

  // Expiration
  hasExpiration: boolean;
  expirationDate?: string;        // ISO date string

  // Linking
  linkedLicenseId?: string;       // Links to a ContractorLicense
  linkedInsuranceId?: string;     // Links to an InsurancePolicy

  // Auto-attach
  autoAttachToNewJobs: boolean;   // Automatically attach to new permit applications
  autoAttachPermitTypes?: string[]; // Only auto-attach for specific permit types

  // Ownership
  contractorId: string;
  uploadedBy: string;

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const REUSABLE_DOCUMENT_LABELS: Record<ReusableDocumentType, string> = {
  license_copy: 'License Copy',
  insurance_coi: 'Certificate of Insurance',
  wc_exemption: 'Workers\' Comp Exemption',
  business_license: 'Business License',
  w9: 'W-9 Form',
  bond: 'Surety Bond',
  letterhead: 'Company Letterhead',
  company_logo: 'Company Logo',
  other: 'Other Document',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a license or insurance is expiring within the given days
 */
export function isExpiringSoon(expirationDate: string, daysThreshold = 30): boolean {
  const expDate = new Date(expirationDate);
  const today = new Date();
  const diffTime = expDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= daysThreshold;
}

/**
 * Check if a license or insurance is expired
 */
export function isExpired(expirationDate: string): boolean {
  const expDate = new Date(expirationDate);
  const today = new Date();
  return expDate < today;
}

/**
 * Get days until expiration (negative if expired)
 */
export function daysUntilExpiration(expirationDate: string): number {
  const expDate = new Date(expirationDate);
  const today = new Date();
  const diffTime = expDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
